import React, { useState, useEffect, useMemo, useRef, Suspense } from 'react';
import { useParams, useLocation, useSearchParams, Routes, Route, Navigate } from 'react-router-dom';
import { m, AnimatePresence, LazyMotion, domAnimation } from 'framer-motion';
// Eager imports — only components that can render on the very first frame of a deep-link entry.
import RentalTiming from './steps/RentalTiming';
import RentalDuration from './steps/RentalDuration';
import DeliveryAreaSelection from './steps/DeliveryAreaSelection';
import Header from './ui/Header';
import { lazyWithReload } from '../utils/lazyWithReload';

// Lazy-load step components that are always 1+ user interactions away.
// Each is preloaded eagerly on mount so the chunk is cached well before the user reaches it.
// lazyWithReload catches the "stale chunk after atomic deploy" failure mode and triggers a
// one-shot reload to fetch the new HTML + chunk graph, instead of dumping users into the
// ErrorBoundary (most visible on post-checkout navigation, when the customer has already paid).
const LocationSelection = lazyWithReload(() => import('./steps/LocationSelection'));
const preloadLocationSelection = () => import('./steps/LocationSelection');

const CategorySelection = lazyWithReload(() => import('./steps/CategorySelection'));
const preloadCategorySelection = () => import('./steps/CategorySelection');

const StartEndSelection = lazyWithReload(() => import('./steps/StartEndSelection'));
const preloadStartEndSelection = () => import('./steps/StartEndSelection');

const DateSelection = lazyWithReload(() => import('./steps/DateSelection'));
const preloadDateSelection = () => import('./steps/DateSelection');

const TimeSelection = lazyWithReload(() => import('./steps/TimeSelection'));
const preloadTimeSelection = () => import('./steps/TimeSelection');

const DeliveryAddress = lazyWithReload(() => import('./steps/DeliveryAddress'));
const preloadDeliveryAddress = () => import('./steps/DeliveryAddress');

const ProductSelection = lazyWithReload(() => import('./steps/ProductSelection'));
const preloadProductSelection = () => import('./steps/ProductSelection');

const Checkout = lazyWithReload(() => import('./steps/Checkout'));
const preloadCheckout = () => import('./steps/Checkout');

const ConfirmationPage = lazyWithReload(() => import('./ConfirmationPage'));
const preloadConfirmationPage = () => import('./ConfirmationPage');

import { debug } from '../utils/debug';
import { captureWarning } from '../utils/sentry';
import { getMerchantData } from '../data/merchants';
import { getMerchantDataFromAPI, fetchProductTypesFromAPI } from '../data/apiAdapter';
import { STRIPE_CONFIG } from '../config/stripe';
import { ThemeProvider } from '../context/ThemeContext';
import { CurrencyProvider, useCurrency } from '../context/CurrencyContext';
import { LanguageProvider } from '../context/LanguageContext';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useNavigateWithLang } from '../hooks/useNavigateWithLang';
import { getCurrentDateInTimezone, getCurrentUTCDate } from '../utils/openingHours';
import { merchantLocalWallToUtc } from '../utils/bookingDateTime';
import { getLocaleFromTimezone } from '../utils/locale';
import { getDeliveryType, DELIVERY_TYPES } from '../utils/deliveryType';
import { getMerchantId as getMerchantIdUtil, buildPath as buildRoutePath } from '../utils/routing';
import { clearCheckoutUserInfo } from '../utils/checkoutFormPersistence';
import { useMotionConfig } from '../hooks/useReducedMotion';

// Utility function to extract available duration objects from pricing rules.
// Returns: [{ id, label, labelTranslations }, ...]
//   - id:                'Nh' | 'Nd' | 'Nw'
//   - label:             merchant-defined label (pricing_rules.duration_label)
//                        in the merchant's default language, or null
//   - labelTranslations: array of { language_code, duration_label } rows from
//                        pricing_rule_translations, or []
//
// Dedup policy across pricing rules that produce the same duration ID:
// first non-null label wins. Conflicts log a warning but do not throw.
const extractAvailableDurations = (products, flowType) => {
  debug.log('📅 [extractAvailableDurations] Input products:', products.length, 'flowType:', flowType || 'any');

  if (!products || products.length === 0) {
    debug.log('📅 [extractAvailableDurations] No products provided, returning empty array');
    return [];
  }

  const durationMap = new Map();

  products.forEach((product, index) => {
    const pricingRules = product._pricingRules || product.pricing_rules || [];
    const activePricingRules = pricingRules.filter(pr => {
      if (pr.active === false) return false;
      // Filter by flow_type: NULL/undefined means both flows
      if (pr.flow_type && flowType && pr.flow_type !== flowType) return false;
      return true;
    });

    debug.log(`📅 [extractAvailableDurations] Product ${index + 1}/${products.length}: "${product.name || product.product_name}"`, {
      totalPricingRules: pricingRules.length,
      activePricingRules: activePricingRules.length,
      rules: activePricingRules.map(r => ({
        duration_type: r.duration_type,
        duration_value: r.duration_value,
        duration_label: r.duration_label || null,
        active: r.active,
        price: r.price
      }))
    });

    activePricingRules.forEach(rule => {
      const durationId = convertPricingRuleToDurationId(rule);
      if (!durationId) {
        debug.warn(`📅 [extractAvailableDurations] Could not convert rule to duration ID:`, rule);
        return;
      }

      const ruleLabel = rule.duration_label && rule.duration_label.trim() !== ''
        ? rule.duration_label
        : null;
      const ruleTranslations = Array.isArray(rule.translations)
        ? rule.translations.filter(tr => tr && tr.duration_label && tr.duration_label.trim() !== '')
        : [];

      const existing = durationMap.get(durationId);
      if (!existing) {
        durationMap.set(durationId, {
          id: durationId,
          label: ruleLabel,
          labelTranslations: ruleTranslations,
          durationType: rule.duration_type || null,
        });
        return;
      }

      if (!existing.label && ruleLabel) {
        existing.label = ruleLabel;
        existing.labelTranslations = ruleTranslations;
        return;
      }

      if (existing.label && ruleLabel && existing.label !== ruleLabel) {
        debug.warn(
          `📅 [extractAvailableDurations] Conflicting duration_label for ${durationId}: "${existing.label}" vs "${ruleLabel}" — keeping first`
        );
      }
    });
  });

  const durations = Array.from(durationMap.values()).sort((a, b) => {
    // Sort by duration value for better UX
    return parseDurationToHours(a.id) - parseDurationToHours(b.id);
  });

  debug.log(
    '📅 [extractAvailableDurations] Final available durations:',
    durations.map(d => `${d.id}${d.label ? ` (label: "${d.label}")` : ''}`)
  );

  return durations;
};

// Convert pricing rule (duration_type, duration_value) to duration ID (e.g., '4h', '1d')
const convertPricingRuleToDurationId = (rule) => {
  const { duration_type, duration_value } = rule;
  const value = parseFloat(duration_value);

  if (duration_type === 'hours') {
    return `${Math.floor(value)}h`;
  } else if (duration_type === 'calendar_day' || duration_type === 'multi_day') {
    const days = Math.floor(value);
    if (days === 7) return '1w';
    if (days === 14) return '2w';
    return `${days}d`;
  } else if (duration_type === 'week') {
    return `${Math.floor(value)}w`;
  }

  return null;
};

// Parse duration ID to hours for sorting
const parseDurationToHours = (durationId) => {
  const match = durationId.match(/^(\d+)([hdw])$/);
  if (!match) return 0;

  const [, value, unit] = match;
  const num = parseInt(value);

  if (unit === 'h') return num;
  if (unit === 'd') return num * 24;
  if (unit === 'w') return num * 24 * 7;

  return 0;
};

const LazyFallback = (
  <div className="flex items-center justify-center py-16">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
  </div>
);

const initialBookingData = {
  location: null,
  timing: null,
  duration: null,
  startDate: null,
  startTime: null,
  selectedProducts: [],
  selectedAddons: [],
  selectedCategories: [], // Multi-select category filter
  deliveryArea: null,
  deliveryAddress: null,
  userInfo: null,
  filters: {
    category: '',
    product: '',
    size: '',
    quantity: 1,
  },
  // Set to true when a deep-link `?model=` URL param successfully seeds the
  // model filter, so TimeSelection / ProductSelection can open their filter
  // panels on arrival. Cleared as soon as the user changes any filter, hits
  // the clear-filter button, or manually closes the filter panel.
  filtersOpenedByUrl: false,
};

const getStepConfig = (timing, deliveryType, hasLocationSlug, location, merchantConfig) => {
  const steps = [];

  // Only include LocationSelection if NOT entering via deep-link
  if (!hasLocationSlug) {
    steps.push({ id: 1, path: 'locations', component: LocationSelection });
  }

  const baseId = hasLocationSlug ? 0 : 1;

  // Check if category step is enabled for this location
  const categoryStepEnabled = location?._apiData?.category_step_enabled || location?.category_step_enabled || false;
  const categoryOffset = categoryStepEnabled ? 1 : 0;

  // Three distinct flows based on delivery type:

  // 1. DELIVERY WITH AREAS (existing model)
  if (deliveryType === DELIVERY_TYPES.DELIVERY_AREAS) {
    steps.push({ id: baseId + 1, path: 'delivery-area', component: DeliveryAreaSelection });
    steps.push({ id: baseId + 2, path: 'duration', component: RentalDuration });
    steps.push({ id: baseId + 3, path: 'date', component: DateSelection });
    steps.push({ id: baseId + 4, path: 'time', component: TimeSelection });

    // Conditionally add category step before products
    if (categoryStepEnabled) {
      steps.push({ id: baseId + 5, path: 'category', component: CategorySelection });
    }

    steps.push({ id: baseId + 5 + categoryOffset, path: 'products', component: ProductSelection });
    steps.push({ id: baseId + 6 + categoryOffset, path: 'delivery-address', component: DeliveryAddress });
    steps.push({ id: baseId + 7 + categoryOffset, path: 'checkout', component: Checkout });
  }

  // 2. DELIVERY WITH DISTANCE/TIME (new model - skips delivery area selection)
  else if (deliveryType === DELIVERY_TYPES.DELIVERY_DISTANCE) {
    steps.push({ id: baseId + 1, path: 'duration', component: RentalDuration });
    steps.push({ id: baseId + 2, path: 'date', component: DateSelection });
    steps.push({ id: baseId + 3, path: 'time', component: TimeSelection });

    // Conditionally add category step before products
    if (categoryStepEnabled) {
      steps.push({ id: baseId + 4, path: 'category', component: CategorySelection });
    }

    steps.push({ id: baseId + 4 + categoryOffset, path: 'products', component: ProductSelection });
    steps.push({ id: baseId + 5 + categoryOffset, path: 'delivery-address', component: DeliveryAddress });
    steps.push({ id: baseId + 6 + categoryOffset, path: 'checkout', component: Checkout });
  }

  // 3. PICKUP (no delivery)
  else {
    steps.push({ id: baseId + 1, path: 'timing', component: RentalTiming });

    // Check if start_end flow variant is enabled (only for reservation, not immediate)
    const useStartEndFlow = merchantConfig?.booking_flow_variant === 'start_end' && timing === 'reservation';

    if (useStartEndFlow) {
      // Alternative flow: single combined start+end picker replaces Duration → Date → Time
      steps.push({ id: baseId + 2, path: 'start-end', component: StartEndSelection });

      if (categoryStepEnabled) {
        steps.push({ id: baseId + 3, path: 'category', component: CategorySelection });
      }

      steps.push({ id: baseId + 3 + categoryOffset, path: 'products', component: ProductSelection });
      steps.push({ id: baseId + 4 + categoryOffset, path: 'checkout', component: Checkout });
    } else {
      steps.push({ id: baseId + 2, path: 'duration', component: RentalDuration });

      if (timing !== 'immediate') {
        // Scheduled rental: date and time first, then category, then products
        steps.push({ id: baseId + 3, path: 'date', component: DateSelection });
        steps.push({ id: baseId + 4, path: 'time', component: TimeSelection });

        // Conditionally add category step before products
        if (categoryStepEnabled) {
          steps.push({ id: baseId + 5, path: 'category', component: CategorySelection });
        }

        steps.push({ id: baseId + 5 + categoryOffset, path: 'products', component: ProductSelection });
        steps.push({ id: baseId + 6 + categoryOffset, path: 'checkout', component: Checkout });
      } else {
        // Immediate rental (no date/time steps)
        // Conditionally add category step before products
        if (categoryStepEnabled) {
          steps.push({ id: baseId + 3, path: 'category', component: CategorySelection });
        }

        steps.push({ id: baseId + 3 + categoryOffset, path: 'products', component: ProductSelection });
        steps.push({ id: baseId + 4 + categoryOffset, path: 'checkout', component: Checkout });
      }
    }
  }

  return steps;
};

const BookingFlow = () => {
  const { '*': wildcardPath } = useParams();
  const navigate = useNavigateWithLang();
  const location = useLocation();
  const motion = useMotionConfig();
  const [searchParams] = useSearchParams();

  // Capture the ?model= URL param once on first render so later re-renders
  // (or back-navigation that re-reads useSearchParams) don't keep retriggering
  // the resolve effect. Trimmed so stray whitespace from copy-pasted links
  // doesn't silently miss the match.
  const urlModelParamRef = useRef(null);
  if (urlModelParamRef.current === null) {
    const raw = searchParams.get('model');
    urlModelParamRef.current = raw ? raw.trim() : '';
  }
  // Tracks whether the URL model has already been resolved for the current
  // location so we don't refetch bikes on every re-render.
  const urlModelResolvedRef = useRef(false);

  // Get merchantId from subdomain (prod) or path (dev/preview)
  const merchantId = getMerchantIdUtil();

  // Extract locationSlug from wildcard path if present
  // URL patterns: /:merchantId/locations OR /:merchantId/:locationSlug/timing
  const pathParts = wildcardPath?.split('/').filter(Boolean) || [];
  const potentialSlug = pathParts[0];
  const stepPath = pathParts[1] || pathParts[0];

  // If first part is a known step (locations, timing, etc.), it's NOT a slug
  const knownSteps = ['locations', 'timing', 'duration', 'date', 'time', 'start-end', 'products', 'checkout', 'delivery-area', 'delivery-address', 'confirmation'];
  const hasLocationSlug = potentialSlug && !knownSteps.includes(potentialSlug);
  const locationSlug = hasLocationSlug ? potentialSlug : null;

  const [merchantData, setMerchantData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableDurations, setAvailableDurations] = useState([]);
  const [locationFromSlug, setLocationFromSlug] = useState(null);

  // Track if user entered via deep-link (set once, never changes during session)
  const [enteredViaDeepLink, setEnteredViaDeepLink] = useState(() => {
    // On initial load, check if we have a location slug AND we're not on the locations page
    const isOnLocationsPage = stepPath === 'locations';
    return hasLocationSlug && !isOnLocationsPage;
  });

  // Update document title and favicon based on merchant data
  useDocumentMeta(merchantData);

  const [bookingData, setBookingData] = useState(() => {
    // Primary store is per-tab sessionStorage. Fallback is a localStorage backup
    // that survives redirect-based payment round-trips (e.g. Pay by Bank on
    // mobile), which can return the user in a fresh tab with sessionStorage
    // wiped — without it, "Try again" after a failed redirect payment dropped
    // the cart and sent the user back to step 1. The backup is time-bounded so a
    // stale cart can't resurface much later, and landing on /locations resets it
    // anyway (see effect below).
    const STORAGE_BACKUP_MAX_AGE_MS = 2 * 60 * 60 * 1000; // 2 hours
    const readSaved = () => {
      try {
        const session = sessionStorage.getItem(`bookingData-${merchantId}`);
        if (session) return JSON.parse(session);
      } catch (e) {
        console.error('Failed to parse session booking data', e);
      }
      try {
        const backupRaw = localStorage.getItem(`bookingDataBackup-${merchantId}`);
        if (backupRaw) {
          const backup = JSON.parse(backupRaw);
          if (backup?.data && backup?.ts && (Date.now() - backup.ts) < STORAGE_BACKUP_MAX_AGE_MS) {
            debug.log('🛟 [STORAGE] sessionStorage empty — restoring cart from localStorage backup');
            return JSON.parse(backup.data);
          }
        }
      } catch (e) {
        console.error('Failed to parse backup booking data', e);
      }
      return null;
    };

    try {
      const parsed = readSaved();
      if (parsed) {
        // If we arrived via deep-link to a specific location, discard saved
        // data that belongs to a different location to avoid stale content.
        if (locationSlug && parsed.location) {
          const savedSlug = parsed.location?._apiData?.location_slug || parsed.location?.slug;
          if (savedSlug && savedSlug !== locationSlug) {
            debug.log('🔗 [SESSION] Discarding stale session data for', savedSlug, '(URL is', locationSlug + ')');
            return { ...initialBookingData };
          }
        }
        if (parsed.startDate) {
          parsed.startDate = new Date(parsed.startDate);
        }
        if (parsed.endDate) {
          parsed.endDate = new Date(parsed.endDate);
        }
        return parsed;
      }
    } catch (e) {
      console.error("Failed to restore booking data", e);
    }
    return { ...initialBookingData };
  });

  useEffect(() => {
    const loadMerchantData = async () => {
      try {
        setLoading(true);
        setError(null);

        debug.log('Loading merchant data for:', merchantId);

        // Try to fetch from API first
        try {
          debug.log('Attempting to fetch from API...');
          const apiData = await getMerchantDataFromAPI(merchantId);
          debug.init(apiData.config?.debug);
          debug.log('Successfully loaded data from API:', apiData);
          setMerchantData(apiData);
          return;
        } catch (apiError) {
          debug.warn('API fetch failed, falling back to hardcoded data:', apiError);
        }

        // Fallback to hardcoded data
        const data = getMerchantData(merchantId);

        if (!data) {
          throw new Error(`Merchant data not found for: ${merchantId}`);
        }

        debug.init(data.config?.debug);
        debug.log('Loaded hardcoded merchant data:', data);
        setMerchantData(data);
      } catch (err) {
        console.error('Error loading merchant data:', err);
        setError(err.message);

        // Final fallback to rideshare if everything fails
        if (merchantId !== 'rideshare') {
          debug.init(false);
          debug.log('Falling back to rideshare merchant');
          const fallbackData = getMerchantData('rideshare');
          setMerchantData(fallbackData);
        }
      } finally {
        setLoading(false);
      }
    };

    loadMerchantData();
  }, [merchantId]);

  // Load location by slug for deep-link flows.
  // navigate is intentionally excluded from deps to avoid re-running on
  // every render (useNavigateWithLang returns a stable ref now, but this
  // is a safety net). navigate is only used in error/redirect paths.
  useEffect(() => {
    const loadLocationBySlug = async () => {
      if (!locationSlug || !merchantData) {
        return;
      }

      debug.log('🔗 [Deep-Link] Loading location by slug:', locationSlug);

      try {
        // Find location by slug in merchant data
        const location = merchantData.locations?.find(loc => {
          const slug = loc._apiData?.location_slug || loc.slug;
          return slug === locationSlug;
        });

        if (!location) {
          console.error('Location not found for slug:', locationSlug);
          // Redirect to location selection if slug is invalid
          navigate(buildPath('/locations'), { replace: true });
          return;
        }

        debug.log('🔗 [Deep-Link] Found location:', location);
        setLocationFromSlug(location);

        // Pre-populate bookingData with this location.
        // If a different location is already loaded (e.g. user navigated to a
        // new deep-link), reset bookingData so stale selections (products,
        // dates, etc.) from the previous location are cleared.
        setBookingData(prev => {
          const prevSlug = prev.location?._apiData?.location_slug || prev.location?.slug;
          const newSlug = location._apiData?.location_slug || location.slug;
          if (!prev.location) {
            debug.log('🔗 [Deep-Link] Pre-populating location in bookingData');
            return { ...prev, location };
          }
          if (prevSlug !== newSlug) {
            debug.log('🔗 [Deep-Link] Location changed from', prevSlug, 'to', newSlug, '- resetting bookingData');
            return { ...initialBookingData, location };
          }
          // Same location as before (typically restored from sessionStorage):
          // overlay the freshly-fetched location so server-side changes
          // (opening_hours, pickup_hours, timeslot interval, tax, etc.) — and the
          // _apiData stripped on the sessionStorage round-trip — are refreshed
          // without requiring the tab to be closed. Nothing user-entered is
          // stored on `location`, so replacing it wholesale is safe.
          debug.log('🔗 [Deep-Link] Refreshing persisted location with fresh server data');
          return { ...prev, location };
        });
      } catch (error) {
        console.error('Error loading location by slug:', error);
        navigate(buildPath('/locations'), { replace: true });
      }
    };

    loadLocationBySlug();
  }, [locationSlug, merchantData, merchantId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the in-flight location in sync with freshly-loaded merchant data.
  // bookingData.location can be rehydrated from sessionStorage (or the
  // localStorage backup) and thus carry stale server-derived fields
  // (opening_hours, pickup_hours, timeslot_interval_minutes, tax, delivery
  // config, etc.) plus an _apiData that was stripped on the sessionStorage
  // round-trip. Whenever merchantData (re)loads, overlay the matching fresh
  // location so config changes take effect without the user having to close the
  // tab. This is the general safety net that also covers entry points without a
  // location slug in the URL (the deep-link effect above handles the slug flow).
  //
  // Match by id first, then slug. If no fresh match is found (location hidden,
  // deactivated, slug changed, or belonging to another merchant), leave the cart
  // untouched — never null out an in-flight selection. Depends only on
  // merchantData, so it runs once per merchant-data load and cannot loop.
  useEffect(() => {
    if (!merchantData?.locations?.length) return;
    setBookingData(prev => {
      const current = prev.location;
      if (!current) return prev;
      const currentId = current._apiData?.id || current.id;
      const currentSlug = current._apiData?.location_slug || current.slug;
      const fresh = merchantData.locations.find(loc => {
        const id = loc._apiData?.id || loc.id;
        const slug = loc._apiData?.location_slug || loc.slug;
        return (currentId && id === currentId) || (currentSlug && slug === currentSlug);
      });
      if (!fresh || fresh === current) return prev;
      debug.log('🔄 [LOCATION SYNC] Overlaying persisted location with fresh server data');
      return { ...prev, location: fresh };
    });
  }, [merchantData]);

  useEffect(() => {
    try {
      debug.log('💾 [SESSION STORAGE] Saving bookingData:', {
        productsCount: bookingData.selectedProducts?.length || 0,
        addonsCount: bookingData.selectedAddons?.length || 0,
        products: bookingData.selectedProducts?.map(p => ({ id: p.id, name: p.name })) || []
      });
      // Use a replacer to strip deeply-nested _apiData from serialization.
      // _apiData preserves raw API responses for backend calls but can be very
      // large/deep and is not needed for session restore (it gets re-fetched).
      const replacer = (key, value) => (key === '_apiData' ? undefined : value);
      const serialized = JSON.stringify(bookingData, replacer);
      sessionStorage.setItem(`bookingData-${merchantId}`, serialized);
      // Mirror to a timestamped localStorage backup so the cart survives a
      // redirect-based payment round-trip that wipes sessionStorage (see the
      // restore initializer above). Wrapped in its own try/catch because
      // localStorage can throw (private mode / quota) without breaking saves.
      try {
        localStorage.setItem(`bookingDataBackup-${merchantId}`, JSON.stringify({ ts: Date.now(), data: serialized }));
      } catch (e) {
        debug.warn('Could not write booking data backup to localStorage', e);
      }
    } catch (e) {
      console.error("Failed to save booking data to session storage", e);
    }
  }, [bookingData, merchantId]);

  // Load available durations from products when location is selected
  useEffect(() => {
    const loadAvailableDurations = async () => {
      const locationId = bookingData.location?._apiData?.id || bookingData.location?.id;

      debug.log('📅 [loadAvailableDurations] Starting...', {
        locationId,
        hasLocation: !!bookingData.location,
        merchantDataFromAPI: merchantData?._fromAPI,
        hasMerchantData: !!merchantData,
        hasBikes: !!merchantData?.bikes
      });

      if (!locationId) {
        debug.log('📅 [loadAvailableDurations] No location ID, clearing durations');
        setAvailableDurations([]);
        return;
      }

      try {
        debug.log('📅 [loadAvailableDurations] Loading available durations for location:', locationId);

        if (merchantData?._fromAPI) {
          // Use lightweight durations API endpoint
          const timing = bookingData.timing;
          // Map booking timing to flow_type: 'immediate' stays 'immediate', 'reservation' maps to 'reservation'
          const flowType = timing === 'immediate' ? 'immediate' : timing === 'reservation' ? 'reservation' : '';
          const flowParam = flowType ? `&flowType=${flowType}` : '';
          debug.log('📅 [loadAvailableDurations] Fetching from durations API...', { flowType: flowType || 'any' });
          const response = await fetch(
            `${STRIPE_CONFIG.apiBaseUrl}/durations?locationId=${locationId}${flowParam}`
          );

          if (!response.ok) {
            throw new Error(`Failed to fetch durations: ${response.statusText}`);
          }

          const { durations, success } = await response.json();

          if (!success || !durations) {
            throw new Error('Invalid response from durations API');
          }

          debug.log('📅 [loadAvailableDurations] Fetched durations from API:', durations);
          setAvailableDurations(durations);
        } else if (merchantData?.bikes) {
          // Fallback: Use hardcoded merchant data
          debug.log('📅 [loadAvailableDurations] Using hardcoded merchant data...');
          const durations = extractAvailableDurations(merchantData.bikes, bookingData.timing);
          debug.log('📅 [loadAvailableDurations] Extracted durations from hardcoded data:', durations);
          setAvailableDurations(durations);
        } else {
          debug.warn('📅 [loadAvailableDurations] No products available - neither API nor hardcoded data');
          setAvailableDurations([]);
        }
      } catch (error) {
        console.error('Error loading available durations:', error);
        setAvailableDurations([]);
      }
    };

    loadAvailableDurations();
  }, [bookingData.location, bookingData.timing, merchantData]);

  // Eagerly preload lazy chunks in the background once the app mounts.
  // By the time the user reaches these steps, the chunks will be cached.
  //
  // These are speculative, so a failure must stay a no-op: deliberately NOT
  // routed through lazyWithReload, whose chunk-error path calls
  // location.reload() — that would reload the landing page on first paint for
  // anyone whose content blocker or flaky connection drops one of these
  // background fetches, before they have clicked anything. Nothing is lost by
  // staying quiet here: a failed module fetch is recorded in the browser's
  // module map, so the same error resurfaces when React's lazy() factory runs
  // at the step the user actually reaches, where lazyWithReload handles it.
  //
  // The .catch() is what keeps a failed preload off the global
  // onunhandledrejection handler. Reported once per mount so a user behind an
  // aggressive blocker produces one warning rather than nine.
  useEffect(() => {
    const preloads = [
      ['locations', preloadLocationSelection],
      ['category', preloadCategorySelection],
      ['start-end', preloadStartEndSelection],
      ['date', preloadDateSelection],
      ['time', preloadTimeSelection],
      ['delivery-address', preloadDeliveryAddress],
      ['products', preloadProductSelection],
      ['checkout', preloadCheckout],
      ['confirmation', preloadConfirmationPage],
    ];

    let reported = false;
    preloads.forEach(([step, preload]) => {
      preload().catch((err) => {
        debug.warn(`Chunk preload failed for '${step}' — deferring to lazy load`, err);
        if (reported) return;
        reported = true;
        captureWarning('Step chunk preload failed', {
          step,
          errorName: err?.name,
          errorMessage: typeof err?.message === 'string' ? err.message.slice(0, 200) : undefined,
          path: typeof window !== 'undefined' ? window.location.href.split('?')[0] : undefined,
        }, { flow: 'reload', stage: 'chunk-preload' });
      });
    });
  }, []);

  // Scroll to top on page change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Clear cart and reset all booking data when on locations page
  useEffect(() => {
    const currentPath = location.pathname.split('/').pop();
    if (currentPath === 'locations') {
      debug.log('On locations page - resetting all booking data');
      // Reset deep-link flag when user navigates back to locations
      setEnteredViaDeepLink(false);
      // Reset to initial booking data (clear everything)
      setBookingData({ ...initialBookingData });
      // Starting over — drop any persisted checkout contact details too, so a
      // shared device doesn't surface a previous customer's name/email/phone.
      clearCheckoutUserInfo(merchantId);
    }
  }, [location.pathname]);

  const updateBookingData = (key, value) => {
    setBookingData(prev => ({ ...prev, [key]: value }));
  };

  // Helper function to prefetch durations for a location
  const prefetchDurations = async (location) => {
    if (!location) return;

    const locationId = location._apiData?.id || location.id;
    debug.log('📅 [prefetchDurations] Prefetching durations for location:', locationId);

    try {
      // Use lightweight durations API endpoint
      const response = await fetch(
        `${STRIPE_CONFIG.apiBaseUrl}/durations?locationId=${locationId}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch durations: ${response.statusText}`);
      }

      const { durations, success } = await response.json();

      if (!success || !durations) {
        throw new Error('Invalid response from durations API');
      }

      debug.log('📅 [prefetchDurations] Prefetched durations:', durations);
      setAvailableDurations(durations);
    } catch (error) {
      console.error('📅 [prefetchDurations] Error prefetching durations:', error);
      setAvailableDurations([]);
    }
  };

  const updateCart = (products, addons) => {
    debug.log('🛒 [UPDATE CART] Called with:', {
      productsCount: products?.length || 0,
      addonsCount: addons?.length || 0,
      products: products?.map(p => ({ id: p.id, name: p.name, size: p.selectedSize })) || [],
      addons: addons?.map(a => ({ id: a.id, name: a.name, quantity: a.quantity })) || []
    });

    setBookingData(prev => ({
      ...prev,
      selectedProducts: products,
      selectedAddons: addons,
    }));
  };

  const handleFiltersChange = (newFilters) => {
    // Any user-initiated filter change (picking a different model, clearing,
    // changing quantity/size/category) also dismisses the auto-open-from-URL
    // flag so the panel doesn't keep reopening on future page navigations.
    setBookingData(prev => ({
      ...prev,
      filters: newFilters,
      filtersOpenedByUrl: false,
    }));
  };

  // Called when the user manually collapses the filter panel — dismisses the
  // auto-open flag so subsequent pages don't reopen it.
  const handleFiltersPanelClosed = () => {
    setBookingData(prev => (
      prev.filtersOpenedByUrl
        ? { ...prev, filtersOpenedByUrl: false }
        : prev
    ));
  };

  const handleCategoriesChange = (newCategories) => {
    setBookingData(prev => ({
      ...prev,
      selectedCategories: newCategories,
    }));
  };

  // Resolve a ?model=... URL param to the matching bike.name so the existing
  // filter machinery (which compares filters.product to bike.name) pre-selects
  // that model automatically. Runs once per location — if the model doesn't
  // exist at this location, silently leaves the filter untouched rather than
  // stranding the user on an empty results page.
  //
  // Matches against product_types.model (case-insensitive, trimmed) because
  // product_name isn't a stable canonical identifier; populating `.model` is
  // the merchant's opt-in mechanism for making shareable deep-link URLs work.
  useEffect(() => {
    if (urlModelResolvedRef.current) return;
    const targetModel = urlModelParamRef.current;
    if (!targetModel) return;
    const locationId = bookingData.location?._apiData?.id || bookingData.location?.id;
    if (!locationId) return;

    urlModelResolvedRef.current = true;
    let cancelled = false;
    const needle = targetModel.toLowerCase();
    (async () => {
      try {
        const bikes = await fetchProductTypesFromAPI(locationId);
        if (cancelled) return;
        const match = (bikes || []).find(
          (b) => typeof b?.model === 'string' && b.model.trim().toLowerCase() === needle
        );
        if (!match) {
          debug.log(`🔗 [DEEP-LINK] No product at location ${locationId} with model="${targetModel}" — ignoring URL filter`);
          return;
        }
        debug.log(`🔗 [DEEP-LINK] Pre-applying model filter from URL: "${targetModel}" → "${match.name}"`);
        setBookingData((prev) => {
          // Only flip filtersOpenedByUrl when the model filter actually
          // transitions from empty → URL value. If a previous session had
          // already touched the filter (and then closed the auto-opened
          // panel), we shouldn't override that choice on re-entry.
          const transitionFromEmpty = !prev.filters?.product;
          return {
            ...prev,
            filters: { ...prev.filters, product: match.name },
            filtersOpenedByUrl: transitionFromEmpty ? true : prev.filtersOpenedByUrl,
          };
        });
      } catch (err) {
        console.error('❌ [DEEP-LINK] Failed to resolve URL model filter:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [bookingData.location]);

  // Determine delivery type for the selected location
  const deliveryType = useMemo(() => {
    return bookingData.location ? getDeliveryType(bookingData.location) : DELIVERY_TYPES.NONE;
  }, [bookingData.location]);

  const isDeliveryLocation = deliveryType !== DELIVERY_TYPES.NONE;
  const isDeliveryAreas = deliveryType === DELIVERY_TYPES.DELIVERY_AREAS;
  const isDeliveryDistance = deliveryType === DELIVERY_TYPES.DELIVERY_DISTANCE;

  const stepConfig = useMemo(() => getStepConfig(bookingData.timing, deliveryType, enteredViaDeepLink, bookingData.location, merchantData?.config), [bookingData.timing, deliveryType, enteredViaDeepLink, bookingData.location, merchantData?.config]);

  // Helper function to build navigation paths with or without locationSlug
  const buildPath = (stepPath) => {
    if (hasLocationSlug && locationSlug) {
      return buildRoutePath(`/${locationSlug}/${stepPath}`, merchantId);
    }
    return buildRoutePath(`/${stepPath}`, merchantId);
  };

  // Helper function to determine if back navigation should be allowed
  const shouldAllowBack = (currentPath) => {
    if (!enteredViaDeepLink) {
      // Normal flow: always allow back navigation
      return true;
    }

    // Deep-link flow: allow back navigation only if not on the first step
    let firstStepPath;
    if (deliveryType === DELIVERY_TYPES.DELIVERY_AREAS) {
      firstStepPath = 'delivery-area';
    } else if (deliveryType === DELIVERY_TYPES.DELIVERY_DISTANCE) {
      firstStepPath = 'duration';
    } else {
      firstStepPath = 'timing';
    }
    return currentPath !== firstStepPath;
  };

  const currentPathEnd = location.pathname.split('/').pop();
  const currentStepInfo = useMemo(() => {
    return stepConfig.find(s => s.path === currentPathEnd);
  }, [currentPathEnd, stepConfig]);

  // Null when the current path isn't a configured step (e.g. confirmation
  // page). Header treats null as post-flow and hides the progress UI.
  const currentStepId = currentStepInfo ? currentStepInfo.id : null;

  // Update document title per step for WCAG 2.4.2 (Page Titled)
  useEffect(() => {
    const stepTitles = {
      'locations': 'Select Location',
      'timing': 'Choose Rental Type',
      'duration': 'Select Duration',
      'date': 'Pick Date',
      'time': 'Select Time',
      'category': 'Choose Category',
      'products': 'Select Products',
      'checkout': 'Checkout',
      'start-end': 'Select Dates & Times',
      'delivery-area': 'Choose Delivery Area',
      'delivery-address': 'Enter Delivery Address',
    };
    const stepTitle = stepTitles[currentPathEnd];
    const merchantName = merchantData?.config?.display_name;
    if (stepTitle && merchantName) {
      document.title = `${stepTitle} - ${merchantName}`;
    } else if (stepTitle) {
      document.title = stepTitle;
    }
  }, [currentPathEnd, merchantData]);

  // Move focus to main content on step navigation for WCAG 2.4.3 (Focus Order)
  useEffect(() => {
    const main = document.getElementById('main-content');
    if (main) {
      main.focus({ preventScroll: true });
      main.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentPathEnd]);

  if (loading) {
    // Capitalize first letter of merchant ID for display
    const capitalizedMerchantId = merchantId
      ? merchantId.charAt(0).toUpperCase() + merchantId.slice(1)
      : '';

    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-gray-600">Loading {capitalizedMerchantId}...</div>
        </div>
      </div>
    );
  }

  if (error && !merchantData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 via-white to-red-50">
        <div className="text-center p-8">
          <div className="text-red-600 text-xl font-semibold mb-2">Error Loading Merchant</div>
          <div className="text-gray-600 mb-4">{error}</div>
          <button
            onClick={() => navigate(buildPath('/locations', 'rideshare'))}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to RideShare
          </button>
        </div>
      </div>
    );
  }

  if (!merchantData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div>No merchant data available</div>
      </div>
    );
  }

  return (
    <LanguageProvider merchantData={merchantData.config}>
      <CurrencyProvider merchantData={merchantData.config}>
        <ThemeProvider merchantData={merchantData}>
          <LazyMotion features={domAnimation} strict>
          <div className="min-h-screen">
            <a
              href="#main-content"
              className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-blue-600 focus:text-white focus:px-4 focus:py-2 focus:rounded-md"
            >
              Skip to main content
            </a>
            <Header
              currentStep={currentStepId}
              bookingData={bookingData}
              merchantData={merchantData}
              enteredViaDeepLink={enteredViaDeepLink}
            />
      <main id="main-content" tabIndex="-1" className="max-w-4xl mx-auto px-4 py-8 outline-none">
        <AnimatePresence mode="wait">
          <m.div
            key={location.pathname}
            {...motion.pageTransition}
          >
            <Routes location={location}>
              <Route
                path="locations"
                element={
                  <Suspense fallback={LazyFallback}>
                    <LocationSelection
                      locations={merchantData.locations}
                      onLocationSelect={location => {
                        updateBookingData('location', location);
                        // Determine delivery type to navigate to correct next step
                        const locDeliveryType = getDeliveryType(location);
                        // Get location slug for URL
                        const slug = location._apiData?.location_slug || location.slug;

                        // Prefetch durations for delivery locations that go directly to duration step
                        // Fire-and-forget: don't await, navigation happens immediately
                        if (locDeliveryType === DELIVERY_TYPES.DELIVERY_DISTANCE) {
                          prefetchDurations(location);
                        }

                        // Navigate based on delivery type:
                        // - DELIVERY_AREAS: delivery-area step
                        // - DELIVERY_DISTANCE: skip delivery-area, go to duration
                        // - NONE: timing step for pickup
                        let nextStep;
                        if (locDeliveryType === DELIVERY_TYPES.DELIVERY_AREAS) {
                          nextStep = 'delivery-area';
                        } else if (locDeliveryType === DELIVERY_TYPES.DELIVERY_DISTANCE) {
                          nextStep = 'duration';
                        } else {
                          nextStep = 'timing';
                        }
                        navigate(buildRoutePath(`/${slug}/${nextStep}`, merchantId));
                      }}
                    />
                  </Suspense>
                }
              />
              <Route
                path="timing"
                element={
                  <RentalTiming
                    selectedTiming={bookingData.timing}
                    location={bookingData.location}
                    merchantConfig={merchantData.config || {}}
                    onTimingSelect={timing => {
                      updateBookingData('timing', timing);
                      const useStartEnd = merchantData?.config?.booking_flow_variant === 'start_end' && timing === 'reservation';
                      navigate(buildPath(useStartEnd ? 'start-end' : 'duration'));
                    }}
                    onBack={shouldAllowBack('timing') ? () => navigate(-1) : undefined}
                  />
                }
              />
              <Route
                path=":locationSlug/timing"
                element={
                  <RentalTiming
                    selectedTiming={bookingData.timing}
                    location={bookingData.location}
                    merchantConfig={merchantData.config || {}}
                    onTimingSelect={timing => {
                      updateBookingData('timing', timing);
                      const useStartEnd = merchantData?.config?.booking_flow_variant === 'start_end' && timing === 'reservation';
                      navigate(buildPath(useStartEnd ? 'start-end' : 'duration'));
                    }}
                    onBack={shouldAllowBack('timing') ? () => navigate(-1) : undefined}
                  />
                }
              />
              {/* Start+End combined picker routes (for start_end flow variant) */}
              <Route
                path="start-end"
                element={
                  <Suspense fallback={LazyFallback}>
                    <StartEndSelection
                      bookingData={bookingData}
                      merchantConfig={merchantData.config || {}}
                      locationId={
                        bookingData.location?._apiData?.id ||
                        bookingData.location?.id
                      }
                      useAPI={merchantData._fromAPI}
                      availableDurations={availableDurations}
                      onStartEndSelect={({ duration, startDate, startTime, endDate }) => {
                        setBookingData(prev => ({
                          ...prev,
                          duration,
                          startDate,
                          startTime,
                          endDate,
                        }));
                        const categoryStepEnabled = bookingData.location?._apiData?.category_step_enabled || bookingData.location?.category_step_enabled || false;
                        navigate(buildPath(categoryStepEnabled ? 'category' : 'products'));
                      }}
                      onBack={shouldAllowBack('start-end') ? () => navigate(-1) : undefined}
                    />
                  </Suspense>
                }
              />
              <Route
                path=":locationSlug/start-end"
                element={
                  <Suspense fallback={LazyFallback}>
                    <StartEndSelection
                      bookingData={bookingData}
                      merchantConfig={merchantData.config || {}}
                      locationId={
                        bookingData.location?._apiData?.id ||
                        bookingData.location?.id
                      }
                      useAPI={merchantData._fromAPI}
                      availableDurations={availableDurations}
                      onStartEndSelect={({ duration, startDate, startTime, endDate }) => {
                        setBookingData(prev => ({
                          ...prev,
                          duration,
                          startDate,
                          startTime,
                          endDate,
                        }));
                        const categoryStepEnabled = bookingData.location?._apiData?.category_step_enabled || bookingData.location?.category_step_enabled || false;
                        navigate(buildPath(categoryStepEnabled ? 'category' : 'products'));
                      }}
                      onBack={shouldAllowBack('start-end') ? () => navigate(-1) : undefined}
                    />
                  </Suspense>
                }
              />
              {/* Delivery area selection routes (only for area-based delivery) */}
              {isDeliveryAreas && (
                <>
                  <Route
                    path="delivery-area"
                    element={
                      <DeliveryAreaSelection
                        bookingData={bookingData}
                        onAreaSelect={area => {
                          updateBookingData('deliveryArea', area);
                          // Prefetch durations (fire-and-forget, don't block navigation)
                          prefetchDurations(bookingData.location);
                          navigate(buildPath('duration'));
                        }}
                        onBack={shouldAllowBack('delivery-area') ? () => navigate(-1) : undefined}
                      />
                    }
                  />
                  <Route
                    path=":locationSlug/delivery-area"
                    element={
                      <DeliveryAreaSelection
                        bookingData={bookingData}
                        onAreaSelect={area => {
                          updateBookingData('deliveryArea', area);
                          // Prefetch durations (fire-and-forget, don't block navigation)
                          prefetchDurations(bookingData.location);
                          navigate(buildPath('duration'));
                        }}
                        onBack={shouldAllowBack('delivery-area') ? () => navigate(-1) : undefined}
                      />
                    }
                  />
                </>
              )}
              <Route
                path="duration"
                element={
                  <RentalDuration
                    merchantConfig={merchantData.config || {}}
                    popularDurations={merchantData.popularDurations || []}
                    availableDurations={availableDurations}
                    bookingData={bookingData}
                    locationId={
                      bookingData.location?._apiData?.id ||
                      bookingData.location?.id
                    }
                    useAPI={merchantData._fromAPI}
                    onDurationSelect={duration => {
                      updateBookingData('duration', duration);

                      // Check if category step is enabled
                      const categoryStepEnabled = bookingData.location?._apiData?.category_step_enabled || bookingData.location?.category_step_enabled || false;

                      // For delivery locations, always go to date first (category comes after time)
                      if (isDeliveryLocation) {
                        navigate(buildPath('date'));
                      } else if (bookingData.timing === 'immediate') {
                        // Immediate pickup: set start time to now
                        const now = getCurrentUTCDate();
                        // Floor to the minute so stored start_date matches the
                        // displayed startTime (which only carries h:mm precision).
                        now.setSeconds(0, 0);

                        // Get merchant timezone for display purposes only
                        const merchantTimezone = merchantData?.config?.timezone;
                        const merchantLocale = getLocaleFromTimezone(merchantTimezone);
                        const displayTime = getCurrentDateInTimezone(merchantTimezone);

                        setBookingData(prev => ({
                          ...prev,
                          startDate: now,  // Use UTC time for booking
                          startTime: {
                            time: displayTime.toLocaleTimeString(merchantLocale, {
                              hour: 'numeric',
                              minute: '2-digit',
                            }),
                            available: true,
                          },
                        }));
                        navigate(buildPath(categoryStepEnabled ? 'category' : 'products'));
                      } else {
                        navigate(buildPath('date'));
                      }
                    }}
                    onBack={shouldAllowBack('duration') ? () => navigate(-1) : undefined}
                  />
                }
              />
              <Route
                path=":locationSlug/duration"
                element={
                  <RentalDuration
                    merchantConfig={merchantData.config || {}}
                    popularDurations={merchantData.popularDurations || []}
                    availableDurations={availableDurations}
                    bookingData={bookingData}
                    locationId={
                      bookingData.location?._apiData?.id ||
                      bookingData.location?.id
                    }
                    useAPI={merchantData._fromAPI}
                    onDurationSelect={duration => {
                      updateBookingData('duration', duration);

                      // Check if category step is enabled
                      const categoryStepEnabled = bookingData.location?._apiData?.category_step_enabled || bookingData.location?.category_step_enabled || false;

                      // For delivery locations, always go to date first (category comes after time)
                      if (isDeliveryLocation) {
                        navigate(buildPath('date'));
                      } else if (bookingData.timing === 'immediate') {
                        // Immediate pickup: set start time to now
                        const now = getCurrentUTCDate();
                        // Floor to the minute so stored start_date matches the
                        // displayed startTime (which only carries h:mm precision).
                        now.setSeconds(0, 0);

                        // Get merchant timezone for display purposes only
                        const merchantTimezone = merchantData?.config?.timezone;
                        const merchantLocale = getLocaleFromTimezone(merchantTimezone);
                        const displayTime = getCurrentDateInTimezone(merchantTimezone);

                        setBookingData(prev => ({
                          ...prev,
                          startDate: now,  // Use UTC time for booking
                          startTime: {
                            time: displayTime.toLocaleTimeString(merchantLocale, {
                              hour: 'numeric',
                              minute: '2-digit',
                            }),
                            available: true,
                          },
                        }));
                        navigate(buildPath(categoryStepEnabled ? 'category' : 'products'));
                      } else {
                        navigate(buildPath('date'));
                      }
                    }}
                    onBack={shouldAllowBack('duration') ? () => navigate(-1) : undefined}
                  />
                }
              />
              {/* Category Selection Routes */}
              <Route
                path="category"
                element={
                  <Suspense fallback={LazyFallback}>
                    <CategorySelection
                      bookingData={bookingData}
                      bikes={merchantData.bikes}
                      onFiltersClear={() => handleFiltersChange({ ...bookingData.filters, product: '', size: '' })}
                      locationId={
                        bookingData.location?._apiData?.id ||
                        bookingData.location?.id
                      }
                      useAPI={merchantData._fromAPI}
                      onCategorySelect={selectedCategories => {
                        debug.log('📂 [BookingFlow] onCategorySelect called with:', selectedCategories);
                        updateBookingData('selectedCategories', selectedCategories);
                        debug.log('📂 [BookingFlow] Updated bookingData, current value:', bookingData.selectedCategories);
                        debug.log('📂 [BookingFlow] Navigating to products...');

                        // Category step always comes directly before products
                        navigate(buildPath('products'));
                      }}
                      onBack={shouldAllowBack('category') ? () => navigate(-1) : undefined}
                    />
                  </Suspense>
                }
              />
              <Route
                path=":locationSlug/category"
                element={
                  <Suspense fallback={LazyFallback}>
                    <CategorySelection
                      bookingData={bookingData}
                      bikes={merchantData.bikes}
                      onFiltersClear={() => handleFiltersChange({ ...bookingData.filters, product: '', size: '' })}
                      locationId={
                        bookingData.location?._apiData?.id ||
                        bookingData.location?.id
                      }
                      useAPI={merchantData._fromAPI}
                      onCategorySelect={selectedCategories => {
                        debug.log('📂 [BookingFlow] onCategorySelect called with:', selectedCategories);
                        updateBookingData('selectedCategories', selectedCategories);
                        debug.log('📂 [BookingFlow] Updated bookingData, current value:', bookingData.selectedCategories);
                        debug.log('📂 [BookingFlow] Navigating to products...');

                        // Category step always comes directly before products
                        navigate(buildPath('products'));
                      }}
                      onBack={shouldAllowBack('category') ? () => navigate(-1) : undefined}
                    />
                  </Suspense>
                }
              />
              {(isDeliveryLocation || bookingData.timing !== 'immediate') && (
                <>
                  <Route
                    path="date"
                    element={
                      <Suspense fallback={LazyFallback}>
                        <DateSelection
                          selectedDate={bookingData.startDate}
                          onDateSelect={date => {
                            updateBookingData('startDate', date);
                            navigate(buildPath('time'));
                          }}
                          onBack={shouldAllowBack('date') ? () => navigate(-1) : undefined}
                          bookingData={bookingData}
                          merchantConfig={merchantData.config || {}}
                          filterOptions={merchantData.filterOptions}
                          bikes={merchantData.bikes}
                          locationId={
                            bookingData.location?._apiData?.id ||
                            bookingData.location?.id ||
                            merchantData.locations?.[0]?._apiData?.id ||
                            merchantData.locations?.[0]?.id
                          }
                          onFiltersChange={handleFiltersChange}
                          useAPI={merchantData._fromAPI}
                          filtersOpenedByUrl={bookingData.filtersOpenedByUrl}
                          onFiltersPanelClosed={handleFiltersPanelClosed}
                        />
                      </Suspense>
                    }
                  />
                  <Route
                    path=":locationSlug/date"
                    element={
                      <Suspense fallback={LazyFallback}>
                        <DateSelection
                          selectedDate={bookingData.startDate}
                          onDateSelect={date => {
                            updateBookingData('startDate', date);
                            navigate(buildPath('time'));
                          }}
                          onBack={shouldAllowBack('date') ? () => navigate(-1) : undefined}
                          bookingData={bookingData}
                          merchantConfig={merchantData.config || {}}
                          filterOptions={merchantData.filterOptions}
                          bikes={merchantData.bikes}
                          locationId={
                            bookingData.location?._apiData?.id ||
                            bookingData.location?.id ||
                            merchantData.locations?.[0]?._apiData?.id ||
                            merchantData.locations?.[0]?.id
                          }
                          onFiltersChange={handleFiltersChange}
                          useAPI={merchantData._fromAPI}
                          filtersOpenedByUrl={bookingData.filtersOpenedByUrl}
                          onFiltersPanelClosed={handleFiltersPanelClosed}
                        />
                      </Suspense>
                    }
                  />
                  <Route
                    path="time"
                    element={
                      <Suspense fallback={LazyFallback}>
                        <TimeSelection
                          bookingData={bookingData}
                          merchantConfig={merchantData.config || {}}
                          onTimeSelect={time => {
                            debug.log('🕒 [BookingFlow] Time selected:', time);
                            debug.log('🕒 [BookingFlow] bookingData.startDate:', bookingData.startDate);

                            // Update startTime
                            updateBookingData('startTime', time);

                            // Calculate proper startDate by combining startDate + selected time
                            if (time.hour !== undefined) {
                              // Use bookingData.startDate (set by DateSelection) or fallback to today.
                              // The picked hour is merchant-local; build the UTC moment via
                              // fromZonedTime so the stored timestamp is correct regardless
                              // of the browser's timezone.
                              const merchantTz = merchantData?.config?.timezone || null;
                              const baseDate = bookingData.startDate
                                ? new Date(bookingData.startDate)
                                : new Date();
                              const calculatedStartDate = merchantLocalWallToUtc(baseDate, time.hour, merchantTz);

                              debug.log('🕒 [BookingFlow] Calculated startDate:', {
                                hadStartDate: !!bookingData.startDate,
                                startDate: bookingData.startDate,
                                timeHour: time.hour,
                                merchantTz,
                                calculatedStartDate: calculatedStartDate.toISOString()
                              });

                              updateBookingData('startDate', calculatedStartDate);
                            }

                            // Check if category step is enabled
                            const categoryStepEnabled = bookingData.location?._apiData?.category_step_enabled || bookingData.location?.category_step_enabled || false;

                            if (categoryStepEnabled) {
                              navigate(buildPath('category'));
                            } else {
                              navigate(buildPath('products'));
                            }
                          }}
                          onFiltersChange={handleFiltersChange}
                          filterOptions={merchantData.filterOptions}
                          bikes={merchantData.bikes}
                          locationId={
                            bookingData.location?._apiData?.id ||
                            bookingData.location?.id ||
                            merchantData.locations?.[0]?._apiData?.id ||
                            merchantData.locations?.[0]?.id
                          }
                          useAPI={merchantData._fromAPI}
                          filtersOpenedByUrl={bookingData.filtersOpenedByUrl}
                          onFiltersPanelClosed={handleFiltersPanelClosed}
                          onBack={shouldAllowBack('time') ? () => navigate(-1) : undefined}
                        />
                      </Suspense>
                    }
                  />
                  <Route
                    path=":locationSlug/time"
                    element={
                      <Suspense fallback={LazyFallback}>
                        <TimeSelection
                          bookingData={bookingData}
                          merchantConfig={merchantData.config || {}}
                          onTimeSelect={time => {
                            debug.log('🕒 [BookingFlow] Time selected:', time);
                            debug.log('🕒 [BookingFlow] bookingData.startDate:', bookingData.startDate);

                            // Update startTime
                            updateBookingData('startTime', time);

                            // Calculate proper startDate by combining startDate + selected time
                            if (time.hour !== undefined) {
                              // Use bookingData.startDate (set by DateSelection) or fallback to today.
                              // The picked hour is merchant-local; build the UTC moment via
                              // fromZonedTime so the stored timestamp is correct regardless
                              // of the browser's timezone.
                              const merchantTz = merchantData?.config?.timezone || null;
                              const baseDate = bookingData.startDate
                                ? new Date(bookingData.startDate)
                                : new Date();
                              const calculatedStartDate = merchantLocalWallToUtc(baseDate, time.hour, merchantTz);

                              debug.log('🕒 [BookingFlow] Calculated startDate:', {
                                hadStartDate: !!bookingData.startDate,
                                startDate: bookingData.startDate,
                                timeHour: time.hour,
                                merchantTz,
                                calculatedStartDate: calculatedStartDate.toISOString()
                              });

                              updateBookingData('startDate', calculatedStartDate);
                            }

                            // Check if category step is enabled
                            const categoryStepEnabled = bookingData.location?._apiData?.category_step_enabled || bookingData.location?.category_step_enabled || false;

                            if (categoryStepEnabled) {
                              navigate(buildPath('category'));
                            } else {
                              navigate(buildPath('products'));
                            }
                          }}
                          onFiltersChange={handleFiltersChange}
                          filterOptions={merchantData.filterOptions}
                          bikes={merchantData.bikes}
                          locationId={
                            bookingData.location?._apiData?.id ||
                            bookingData.location?.id ||
                            merchantData.locations?.[0]?._apiData?.id ||
                            merchantData.locations?.[0]?.id
                          }
                          useAPI={merchantData._fromAPI}
                          filtersOpenedByUrl={bookingData.filtersOpenedByUrl}
                          onFiltersPanelClosed={handleFiltersPanelClosed}
                          onBack={shouldAllowBack('time') ? () => navigate(-1) : undefined}
                        />
                      </Suspense>
                    }
                  />
                </>
              )}
              <Route
                path="products"
                element={
                  <Suspense fallback={LazyFallback}>
                    <ProductSelection
                      bookingData={bookingData}
                      bikes={merchantData.bikes}
                      addons={merchantData.addons}
                      filterOptions={merchantData.filterOptions}
                      locationId={
                        bookingData.location?._apiData?.id ||
                        bookingData.location?.id ||
                        merchantData.locations?.[0]?._apiData?.id ||
                        merchantData.locations?.[0]?.id
                      }
                      useAPI={merchantData._fromAPI}
                      onProductsSelect={(products, addons) => {
                        updateCart(products, addons);
                        // For delivery locations, go to delivery address first; otherwise go to checkout
                        const nextStep = isDeliveryLocation ? 'delivery-address' : 'checkout';
                        navigate(buildPath(nextStep));
                      }}
                      onUpdateCart={updateCart}
                      onFiltersChange={handleFiltersChange}
                      onCategoriesChange={handleCategoriesChange}
                      filtersOpenedByUrl={bookingData.filtersOpenedByUrl}
                      onFiltersPanelClosed={handleFiltersPanelClosed}
                      onBack={shouldAllowBack('products') ? () => navigate(-1) : undefined}
                      onGoToTiming={() => navigate(buildPath('timing'))}
                      initialSelectedProducts={bookingData.selectedProducts}
                      initialAddons={bookingData.selectedAddons}
                    />
                  </Suspense>
                }
              />
              <Route
                path=":locationSlug/products"
                element={
                  <Suspense fallback={LazyFallback}>
                    <ProductSelection
                      bookingData={bookingData}
                      bikes={merchantData.bikes}
                      addons={merchantData.addons}
                      filterOptions={merchantData.filterOptions}
                      locationId={
                        bookingData.location?._apiData?.id ||
                        bookingData.location?.id ||
                        merchantData.locations?.[0]?._apiData?.id ||
                        merchantData.locations?.[0]?.id
                      }
                      useAPI={merchantData._fromAPI}
                      onProductsSelect={(products, addons) => {
                        updateCart(products, addons);
                        // For delivery locations, go to delivery address first; otherwise go to checkout
                        const nextStep = isDeliveryLocation ? 'delivery-address' : 'checkout';
                        navigate(buildPath(nextStep));
                      }}
                      onUpdateCart={updateCart}
                      onFiltersChange={handleFiltersChange}
                      onCategoriesChange={handleCategoriesChange}
                      filtersOpenedByUrl={bookingData.filtersOpenedByUrl}
                      onFiltersPanelClosed={handleFiltersPanelClosed}
                      onBack={shouldAllowBack('products') ? () => navigate(-1) : undefined}
                      onGoToTiming={() => navigate(buildPath('timing'))}
                      initialSelectedProducts={bookingData.selectedProducts}
                      initialAddons={bookingData.selectedAddons}
                    />
                  </Suspense>
                }
              />
              {isDeliveryLocation && (
                <>
                  <Route
                    path="delivery-address"
                    element={
                      <Suspense fallback={LazyFallback}>
                        <DeliveryAddress
                          bookingData={bookingData}
                          onAddressSubmit={address => {
                            updateBookingData('deliveryAddress', address);
                            navigate(buildPath('checkout'));
                          }}
                          onBack={shouldAllowBack('delivery-address') ? () => navigate(-1) : undefined}
                        />
                      </Suspense>
                    }
                  />
                  <Route
                    path=":locationSlug/delivery-address"
                    element={
                      <Suspense fallback={LazyFallback}>
                        <DeliveryAddress
                          bookingData={bookingData}
                          onAddressSubmit={address => {
                            updateBookingData('deliveryAddress', address);
                            navigate(buildPath('checkout'));
                          }}
                          onBack={shouldAllowBack('delivery-address') ? () => navigate(-1) : undefined}
                        />
                      </Suspense>
                    }
                  />
                </>
              )}
              <Route
                path="checkout"
                element={
                  <Suspense fallback={LazyFallback}>
                    <Checkout
                      bookingData={bookingData}
                      merchantData={merchantData}
                      onUserInfoUpdate={userInfo => updateBookingData('userInfo', userInfo)}
                      onCustomFieldsChange={values => updateBookingData('customFieldValues', values)}
                      onBack={shouldAllowBack('checkout') ? () => navigate(-1) : undefined}
                      onUpdateCart={updateCart}
                      onUpdateStartTime={(startDate, startTime) =>
                        setBookingData(prev => ({ ...prev, startDate, startTime }))
                      }
                    />
                  </Suspense>
                }
              />
              <Route
                path=":locationSlug/checkout"
                element={
                  <Suspense fallback={LazyFallback}>
                    <Checkout
                      bookingData={bookingData}
                      merchantData={merchantData}
                      onUserInfoUpdate={userInfo => updateBookingData('userInfo', userInfo)}
                      onCustomFieldsChange={values => updateBookingData('customFieldValues', values)}
                      onBack={shouldAllowBack('checkout') ? () => navigate(-1) : undefined}
                      onUpdateCart={updateCart}
                      onUpdateStartTime={(startDate, startTime) =>
                        setBookingData(prev => ({ ...prev, startDate, startTime }))
                      }
                    />
                  </Suspense>
                }
              />
              <Route
                path="confirmation/:orderId"
                element={
                  <Suspense fallback={LazyFallback}>
                    <ConfirmationPage />
                  </Suspense>
                }
              />
              <Route path="*" element={<Navigate to="locations" replace />} />
            </Routes>
          </m.div>
        </AnimatePresence>
      </main>
    </div>
          </LazyMotion>
        </ThemeProvider>
      </CurrencyProvider>
    </LanguageProvider>
  );
};

export default BookingFlow;